<template>
  <div class="nd-page">
    <section class="nd-stat-grid">
      <div class="nd-stat-card">
        <span class="nd-stat-card__label">Dentes registrados</span>
        <strong class="nd-stat-card__value">{{ selectedTeeth.length }}</strong>
        <span class="nd-stat-card__hint">com dados clínicos</span>
      </div>
      <div class="nd-stat-card">
        <span class="nd-stat-card__label">Paciente</span>
        <strong class="nd-stat-card__value nd-stat-card__value--date">{{ selectedPatientName }}</strong>
        <span class="nd-stat-card__hint">vínculo clínico</span>
      </div>
      <div class="nd-stat-card">
        <span class="nd-stat-card__label">Procedimento</span>
        <strong class="nd-stat-card__value nd-stat-card__value--date">{{ selectedProcedureName }}</strong>
        <span class="nd-stat-card__hint">planejamento associado</span>
      </div>
      <div class="nd-stat-card">
        <span class="nd-stat-card__label">Dente selecionado</span>
        <strong class="nd-stat-card__value nd-stat-card__value--date">{{ currentTooth ? `Dente ${currentTooth}` : "-" }}</strong>
        <span class="nd-stat-card__hint">clique no mapa</span>
      </div>
    </section>

    <v-card class="nd-section-card odonto-editor">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">{{ isEditing ? "Editar odontograma" : "Novo odontograma" }}</div>
          <div class="nd-table-toolbar__subtitle">Registre dentes, regiões e observações com o mesmo formato usado no app.</div>
        </div>
      </div>

      <v-form ref="form" v-model="formValid" class="odonto-form">
        <div class="odonto-form__grid">
          <v-text-field
            v-model="form.name"
            label="Nome do odontograma"
            outlined
            dense
            :rules="[rules.required]"
           hide-details="auto"/>

          <v-autocomplete
            v-model="form.patientId"
            :items="patientItems"
            :search-input.sync="patientSearch"
            item-text="name"
            item-value="id"
            label="Paciente"
            outlined
            dense
            clearable
            hide-no-data
           hide-details="auto"/>

          <v-select
            v-model="form.procedureId"
            :items="procedureItems"
            item-text="name"
            item-value="id"
            label="Procedimento"
            outlined
            dense
            clearable
           hide-details="auto"/>

          <v-select
            v-model="form.specialtyId"
            :items="specialtyItems"
            item-text="name"
            item-value="id"
            label="Especialidade"
            outlined
            dense
            clearable
           hide-details="auto"/>
        </div>
      </v-form>

      <div class="odonto-workspace">
        <div class="odonto-chart">
          <div class="odonto-jaw-label">Superior</div>
          <div class="odonto-row">
            <div class="odonto-quadrant">
              <v-menu v-for="tooth in upperRight" :key="tooth" offset-y nudge-bottom="8" :close-on-content-click="false" content-class="odonto-tooth-menu">
                <template #activator="{ on, attrs }">
                  <button type="button" class="odonto-tooth" :data-tooth="tooth" :class="toothClasses(tooth)" :style="toothStyle(tooth)" v-bind="attrs" v-on="on" @click="openTooth(tooth)">
                    <img class="odonto-tooth__image" :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                    <span>{{ tooth }}</span>
                    <i v-for="part in selectedParts(tooth)" :key="part" :class="`part part-${part}`"></i>
                    <b v-for="procedure in visibleProcedures(tooth)" :key="procedure" class="odonto-tooth__procedure" :class="`odonto-tooth__procedure--${procedure}`">
                      {{ procedureShortLabel(procedure) }}
                    </b>
                  </button>
                </template>
                <div class="odonto-tooth-dropdown">
                  <div class="odonto-dropdown__header">
                    <div class="odonto-dropdown__preview" :style="toothStyle(tooth)">
                      <img :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                      <span>{{ tooth }}</span>
                    </div>
                    <div>
                      <div class="odonto-dropdown__title">Dente {{ tooth }}</div>
                      <div class="odonto-dropdown__subtitle">{{ toothSummary(tooth) }}</div>
                    </div>
                  </div>

                  <div class="odonto-dropdown__body">
                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Status clínico</div>
                      <div class="odonto-dropdown__status-grid">
                        <button v-for="status in statuses" :key="status.value" type="button" class="odonto-dropdown__status" :class="{ active: statusByTooth(tooth).value === status.value }" :data-status="status.value" @click="setToothStatus(tooth, status.value)">
                          <span :style="{ background: status.color }"></span>
                          {{ status.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Regiões do dente</div>
                      <div class="odonto-dropdown__parts">
                        <button v-for="part in toothParts" :key="part.value" type="button" :class="{ active: hasPart(tooth, part.value) }" :data-part="part.value" @click="togglePart(tooth, part.value)">
                          {{ part.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Achados e procedimentos</div>
                      <div class="odonto-dropdown__procedures">
                        <button v-for="procedure in toothProceduresOptions" :key="procedure.value" type="button" :class="{ active: hasProcedure(tooth, procedure.value) }" :data-procedure="procedure.value" @click="toggleProcedure(tooth, procedure.value)">
                          <span :style="{ background: procedure.color }">{{ procedure.short }}</span>
                          {{ procedure.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Observação clínica</div>
                      <v-textarea :value="observations[String(tooth)] || observations[tooth]" placeholder="Adicionar observação sobre este dente" outlined dense rows="3" hide-details="auto" @input="setObservation(tooth, $event)" />
                    </div>
                  </div>
                  <div class="odonto-dropdown__footer">
                    <v-btn block outlined color="error" elevation="0" @click="clearTooth(tooth)">
                      Limpar dente
                    </v-btn>
                  </div>
                </div>
              </v-menu>
            </div>
            <div class="odonto-midline"></div>
            <div class="odonto-quadrant">
              <v-menu v-for="tooth in upperLeft" :key="tooth" offset-y nudge-bottom="8" :close-on-content-click="false" content-class="odonto-tooth-menu">
                <template #activator="{ on, attrs }">
                  <button type="button" class="odonto-tooth" :data-tooth="tooth" :class="toothClasses(tooth)" :style="toothStyle(tooth)" v-bind="attrs" v-on="on" @click="openTooth(tooth)">
                    <img class="odonto-tooth__image" :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                    <span>{{ tooth }}</span>
                    <i v-for="part in selectedParts(tooth)" :key="part" :class="`part part-${part}`"></i>
                    <b v-for="procedure in visibleProcedures(tooth)" :key="procedure" class="odonto-tooth__procedure" :class="`odonto-tooth__procedure--${procedure}`">
                      {{ procedureShortLabel(procedure) }}
                    </b>
                  </button>
                </template>
                <div class="odonto-tooth-dropdown">
                  <div class="odonto-dropdown__header">
                    <div class="odonto-dropdown__preview" :style="toothStyle(tooth)">
                      <img :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                      <span>{{ tooth }}</span>
                    </div>
                    <div>
                      <div class="odonto-dropdown__title">Dente {{ tooth }}</div>
                      <div class="odonto-dropdown__subtitle">{{ toothSummary(tooth) }}</div>
                    </div>
                  </div>

                  <div class="odonto-dropdown__body">
                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Status clínico</div>
                      <div class="odonto-dropdown__status-grid">
                        <button v-for="status in statuses" :key="status.value" type="button" class="odonto-dropdown__status" :class="{ active: statusByTooth(tooth).value === status.value }" :data-status="status.value" @click="setToothStatus(tooth, status.value)">
                          <span :style="{ background: status.color }"></span>
                          {{ status.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Regiões do dente</div>
                      <div class="odonto-dropdown__parts">
                        <button v-for="part in toothParts" :key="part.value" type="button" :class="{ active: hasPart(tooth, part.value) }" :data-part="part.value" @click="togglePart(tooth, part.value)">
                          {{ part.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Achados e procedimentos</div>
                      <div class="odonto-dropdown__procedures">
                        <button v-for="procedure in toothProceduresOptions" :key="procedure.value" type="button" :class="{ active: hasProcedure(tooth, procedure.value) }" :data-procedure="procedure.value" @click="toggleProcedure(tooth, procedure.value)">
                          <span :style="{ background: procedure.color }">{{ procedure.short }}</span>
                          {{ procedure.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Observação clínica</div>
                      <v-textarea :value="observations[String(tooth)] || observations[tooth]" placeholder="Adicionar observação sobre este dente" outlined dense rows="3" hide-details="auto" @input="setObservation(tooth, $event)" />
                    </div>
                  </div>
                  <div class="odonto-dropdown__footer">
                    <v-btn block outlined color="error" elevation="0" @click="clearTooth(tooth)">
                      Limpar dente
                    </v-btn>
                  </div>
                </div>
              </v-menu>
            </div>
          </div>

          <div class="odonto-row odonto-row--lower">
            <div class="odonto-quadrant">
              <v-menu v-for="tooth in lowerRight" :key="tooth" offset-y nudge-bottom="8" :close-on-content-click="false" content-class="odonto-tooth-menu">
                <template #activator="{ on, attrs }">
                  <button type="button" class="odonto-tooth" :data-tooth="tooth" :class="toothClasses(tooth)" :style="toothStyle(tooth)" v-bind="attrs" v-on="on" @click="openTooth(tooth)">
                    <img class="odonto-tooth__image" :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                    <span>{{ tooth }}</span>
                    <i v-for="part in selectedParts(tooth)" :key="part" :class="`part part-${part}`"></i>
                    <b v-for="procedure in visibleProcedures(tooth)" :key="procedure" class="odonto-tooth__procedure" :class="`odonto-tooth__procedure--${procedure}`">
                      {{ procedureShortLabel(procedure) }}
                    </b>
                  </button>
                </template>
                <div class="odonto-tooth-dropdown">
                  <div class="odonto-dropdown__header">
                    <div class="odonto-dropdown__preview" :style="toothStyle(tooth)">
                      <img :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                      <span>{{ tooth }}</span>
                    </div>
                    <div>
                      <div class="odonto-dropdown__title">Dente {{ tooth }}</div>
                      <div class="odonto-dropdown__subtitle">{{ toothSummary(tooth) }}</div>
                    </div>
                  </div>

                  <div class="odonto-dropdown__body">
                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Status clínico</div>
                      <div class="odonto-dropdown__status-grid">
                        <button v-for="status in statuses" :key="status.value" type="button" class="odonto-dropdown__status" :class="{ active: statusByTooth(tooth).value === status.value }" :data-status="status.value" @click="setToothStatus(tooth, status.value)">
                          <span :style="{ background: status.color }"></span>
                          {{ status.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Regiões do dente</div>
                      <div class="odonto-dropdown__parts">
                        <button v-for="part in toothParts" :key="part.value" type="button" :class="{ active: hasPart(tooth, part.value) }" :data-part="part.value" @click="togglePart(tooth, part.value)">
                          {{ part.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Achados e procedimentos</div>
                      <div class="odonto-dropdown__procedures">
                        <button v-for="procedure in toothProceduresOptions" :key="procedure.value" type="button" :class="{ active: hasProcedure(tooth, procedure.value) }" :data-procedure="procedure.value" @click="toggleProcedure(tooth, procedure.value)">
                          <span :style="{ background: procedure.color }">{{ procedure.short }}</span>
                          {{ procedure.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Observação clínica</div>
                      <v-textarea :value="observations[String(tooth)] || observations[tooth]" placeholder="Adicionar observação sobre este dente" outlined dense rows="3" hide-details="auto" @input="setObservation(tooth, $event)" />
                    </div>
                  </div>
                  <div class="odonto-dropdown__footer">
                    <v-btn block outlined color="error" elevation="0" @click="clearTooth(tooth)">
                      Limpar dente
                    </v-btn>
                  </div>
                </div>
              </v-menu>
            </div>
            <div class="odonto-midline"></div>
            <div class="odonto-quadrant">
              <v-menu v-for="tooth in lowerLeft" :key="tooth" offset-y nudge-bottom="8" :close-on-content-click="false" content-class="odonto-tooth-menu">
                <template #activator="{ on, attrs }">
                  <button type="button" class="odonto-tooth" :data-tooth="tooth" :class="toothClasses(tooth)" :style="toothStyle(tooth)" v-bind="attrs" v-on="on" @click="openTooth(tooth)">
                    <img class="odonto-tooth__image" :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                    <span>{{ tooth }}</span>
                    <i v-for="part in selectedParts(tooth)" :key="part" :class="`part part-${part}`"></i>
                    <b v-for="procedure in visibleProcedures(tooth)" :key="procedure" class="odonto-tooth__procedure" :class="`odonto-tooth__procedure--${procedure}`">
                      {{ procedureShortLabel(procedure) }}
                    </b>
                  </button>
                </template>
                <div class="odonto-tooth-dropdown">
                  <div class="odonto-dropdown__header">
                    <div class="odonto-dropdown__preview" :style="toothStyle(tooth)">
                      <img :src="toothImage(tooth)" :alt="`Dente ${tooth}`" />
                      <span>{{ tooth }}</span>
                    </div>
                    <div>
                      <div class="odonto-dropdown__title">Dente {{ tooth }}</div>
                      <div class="odonto-dropdown__subtitle">{{ toothSummary(tooth) }}</div>
                    </div>
                  </div>

                  <div class="odonto-dropdown__body">
                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Status clínico</div>
                      <div class="odonto-dropdown__status-grid">
                        <button v-for="status in statuses" :key="status.value" type="button" class="odonto-dropdown__status" :class="{ active: statusByTooth(tooth).value === status.value }" :data-status="status.value" @click="setToothStatus(tooth, status.value)">
                          <span :style="{ background: status.color }"></span>
                          {{ status.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Regiões do dente</div>
                      <div class="odonto-dropdown__parts">
                        <button v-for="part in toothParts" :key="part.value" type="button" :class="{ active: hasPart(tooth, part.value) }" :data-part="part.value" @click="togglePart(tooth, part.value)">
                          {{ part.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Achados e procedimentos</div>
                      <div class="odonto-dropdown__procedures">
                        <button v-for="procedure in toothProceduresOptions" :key="procedure.value" type="button" :class="{ active: hasProcedure(tooth, procedure.value) }" :data-procedure="procedure.value" @click="toggleProcedure(tooth, procedure.value)">
                          <span :style="{ background: procedure.color }">{{ procedure.short }}</span>
                          {{ procedure.label }}
                        </button>
                      </div>
                    </div>

                    <div class="odonto-dropdown__group">
                      <div class="odonto-dropdown__section">Observação clínica</div>
                      <v-textarea :value="observations[String(tooth)] || observations[tooth]" placeholder="Adicionar observação sobre este dente" outlined dense rows="3" hide-details="auto" @input="setObservation(tooth, $event)" />
                    </div>
                  </div>
                  <div class="odonto-dropdown__footer">
                    <v-btn block outlined color="error" elevation="0" @click="clearTooth(tooth)">
                      Limpar dente
                    </v-btn>
                  </div>
                </div>
              </v-menu>
            </div>
          </div>
          <div class="odonto-jaw-label">Inferior</div>
        </div>
      </div>

      <div class="odonto-records" v-if="selectedTeeth.length">
        <div class="odonto-records__title">Dentes com registro</div>
        <div class="odonto-record-grid">
          <div v-for="tooth in selectedTeeth" :key="tooth" class="odonto-record">
            <strong>Dente {{ tooth }}</strong>
            <span :style="{ color: statusByTooth(tooth).color }">{{ statusByTooth(tooth).label }}</span>
            <small v-if="selectedParts(tooth).length">Regiões: {{ selectedParts(tooth).map(partLabel).join(", ") }}</small>
            <small v-if="selectedProcedures(tooth).length">Procedimentos: {{ selectedProcedures(tooth).map(procedureLabel).join(", ") }}</small>
            <small v-if="observations[tooth]">Obs.: {{ observations[tooth] }}</small>
          </div>
        </div>
      </div>

      <div class="nd-form-actions">
        <v-btn outlined color="primary" elevation="0" @click="$router.push('/odontogramas')">Voltar</v-btn>
        <v-btn color="primary" elevation="0" :loading="saving" @click="save">Salvar</v-btn>
      </div>
    </v-card>

    <v-overlay :value="loadingPage">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";

const STATUSES = [
  { value: 1, label: "Tratamento", color: "#e53935" },
  { value: 2, label: "Tratado", color: "#1e88e5" },
  { value: 3, label: "Sadio", color: "#43a047" },
  { value: 4, label: "Ausente", color: "#212121" },
];

const TOOTH_PARTS = [
  { value: "top", label: "Topo" },
  { value: "right", label: "Direita" },
  { value: "bottom", label: "Base" },
  { value: "left", label: "Esquerda" },
  { value: "center", label: "Centro" },
];

const TOOTH_PROCEDURES = [
  { value: "caries", label: "Cárie", short: "CA", color: "#8d6e63" },
  { value: "restoration", label: "Restauração", short: "RE", color: "#00a99d" },
  { value: "crown", label: "Coroa", short: "CO", color: "#d9a441" },
  { value: "root_canal", label: "Canal", short: "CN", color: "#7e57c2" },
  { value: "extraction", label: "Extração/remoção", short: "EX", color: "#d64b4b" },
  { value: "implant", label: "Implante", short: "IM", color: "#2f80ed" },
  { value: "bridge", label: "Ponte/prótese fixa", short: "PF", color: "#546e7a" },
  { value: "veneer", label: "Faceta/lente", short: "FA", color: "#26a69a" },
  { value: "fracture", label: "Fratura", short: "FR", color: "#f2994a" },
  { value: "mobility", label: "Mobilidade", short: "MO", color: "#c2185b" },
];

const UPPER_TOOTH_IMAGES = {
  18: "1 cima d.svg",
  17: "2 cima d.svg",
  16: "3 cima d.svg",
  15: "4 cima d.svg",
  14: "5 cima d.svg",
  13: "6 cima d.svg",
  12: "7 cima d.svg",
  11: "8 cima d.svg",
  21: "9 cima d.svg",
  22: "10 cima d.svg",
  23: "11 cima d.svg",
  24: "12 cima d.svg",
  25: "13 cima d.svg",
  26: "14 cima d.svg",
  27: "15 cima d.svg",
  28: "16 cima d.svg",
};

const LOWER_TOOTH_IMAGES = {
  48: "1 baixo d.svg",
  47: "2 baixo d.svg",
  46: "3 baixo d.svg",
  45: "4 baixo d.svg",
  44: "5 baixo d.svg",
  43: "6 baixo d.svg",
  42: "7 baixo d.svg",
  41: "8 baixo d.svg",
  31: "9 baixo d.svg",
  32: "10 baixo d.svg",
  33: "11 baixo d.svg",
  34: "12 baixo d.svg",
  35: "13 baixo d.svg",
  36: "14 baixo d.svg",
  37: "15 baixo d.svg",
  38: "16 baixo d.svg",
};

export default {
  name: "OdontogramsDetails",

  data: () => ({
    formValid: false,
    loadingPage: false,
    saving: false,
    patientSearch: "",
    patientSearchTimeout: null,
    patientItems: [],
    procedureItems: [],
    specialtyItems: [],
    currentTooth: null,
    toothStatus: {},
    toothPartStatus: {},
    toothProcedures: {},
    observations: {},
    form: {
      id: null,
      name: "",
      patientId: null,
      procedureId: null,
      specialtyId: null,
    },
    statuses: STATUSES,
    toothParts: TOOTH_PARTS,
    toothProceduresOptions: TOOTH_PROCEDURES,
    upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
    upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
    lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
    lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
    rules: {
      required: (value) => !!value || "Campo obrigatório",
    },
  }),

  computed: {
    isEditing() {
      return Boolean(this.form.id);
    },
    selectedTeeth() {
      const teeth = new Set([
        ...Object.keys(this.toothStatus),
        ...Object.keys(this.toothPartStatus),
        ...Object.keys(this.toothProcedures),
        ...Object.keys(this.observations).filter((key) => this.observations[key]),
      ]);
      return Array.from(teeth).map(Number).sort((a, b) => a - b);
    },
    selectedPatientName() {
      return this.patientItems.find((item) => item.id === this.form.patientId)?.name || "-";
    },
    selectedProcedureName() {
      return this.procedureItems.find((item) => item.id === this.form.procedureId)?.name || "-";
    },
  },

  watch: {
    patientSearch(value) {
      clearTimeout(this.patientSearchTimeout);
      this.patientSearchTimeout = setTimeout(() => this.searchPatients(value), 300);
    },
  },

  async created() {
    this.loadingPage = true;
    await Promise.all([this.searchPatients(""), this.loadProcedures(), this.loadSpecialties()]);
    const id = this.$route.params.id;
    if (id) await this.loadOdontogram(id);
    this.loadingPage = false;
  },

  methods: {
    async searchPatients(search) {
      try {
        const response = await axios.get("/patients", { params: { start: 0, limit: 20, search } });
        this.patientItems = response.data.data || [];
      } catch {
        this.patientItems = [];
      }
    },
    async loadProcedures() {
      try {
        const response = await axios.get("/procedures", { params: { start: 0, limit: 100 } });
        this.procedureItems = response.data.data || [];
      } catch {
        this.procedureItems = [];
      }
    },
    async loadSpecialties() {
      try {
        const response = await axios.get("/specialties", { params: { start: 0, limit: 100 } });
        this.specialtyItems = response.data.data || [];
      } catch {
        this.specialtyItems = [];
      }
    },
    async loadOdontogram(id) {
      try {
        const response = await axios.get(`/odontogram/${id}`);
        const data = response.data.data || {};
        this.form = {
          id: data.id,
          name: data.name || "",
          patientId: data.patient?.id || data.patientId || null,
          procedureId: data.procedureId || null,
          specialtyId: data.specialtyId || null,
        };
        if (data.patient && !this.patientItems.some((item) => item.id === data.patient.id)) {
          this.patientItems.push(data.patient);
        }
        this.toothStatus = { ...(data.toothStatus || {}) };
        if (!data.toothStatus && data.toothNumber) {
          data.toothNumber.split(",").map((value) => Number(value.trim())).filter(Boolean).forEach((tooth) => {
            this.$set(this.toothStatus, tooth, 1);
          });
        }
        this.toothPartStatus = { ...(data.toothPartStatus || {}) };
        this.toothProcedures = { ...(data.toothProcedures || {}) };
        this.observations = { ...(data.toothObservations || {}) };
        this.currentTooth = this.selectedTeeth[0] || null;
      } catch {
        this.$dialog.notify.error("Erro ao carregar odontograma.", { position: "top-right", timeout: 5000 });
      }
    },
    openTooth(tooth) {
      this.currentTooth = tooth;
    },
    setToothStatus(tooth, status) {
      this.currentTooth = tooth;
      this.$set(this.toothStatus, String(tooth), status);
    },
    setObservation(tooth, value) {
      const key = String(tooth);
      this.currentTooth = tooth;
      if (value) this.$set(this.observations, key, value);
      else this.$delete(this.observations, key);
    },
    clearTooth(tooth) {
      const key = String(tooth);
      this.$delete(this.toothStatus, key);
      this.$delete(this.toothPartStatus, key);
      this.$delete(this.toothProcedures, key);
      this.$delete(this.observations, key);
      if (this.currentTooth === tooth) this.currentTooth = null;
    },
    togglePart(tooth, part) {
      const key = String(tooth);
      this.currentTooth = tooth;
      const parts = [...(this.toothPartStatus[key] || this.toothPartStatus[tooth] || [])];
      const next = parts.includes(part) ? parts.filter((item) => item !== part) : [...parts, part];
      if (next.length) this.$set(this.toothPartStatus, key, next);
      else this.$delete(this.toothPartStatus, key);
    },
    toggleProcedure(tooth, procedure) {
      const key = String(tooth);
      this.currentTooth = tooth;
      const procedures = [...(this.toothProcedures[key] || this.toothProcedures[tooth] || [])];
      const next = procedures.includes(procedure) ? procedures.filter((item) => item !== procedure) : [...procedures, procedure];
      if (next.length) this.$set(this.toothProcedures, key, next);
      else this.$delete(this.toothProcedures, key);
    },
    hasPart(tooth, part) {
      return this.selectedParts(tooth).includes(part);
    },
    hasProcedure(tooth, procedure) {
      return this.selectedProcedures(tooth).includes(procedure);
    },
    selectedParts(tooth) {
      return this.toothPartStatus[tooth] || this.toothPartStatus[String(tooth)] || [];
    },
    selectedProcedures(tooth) {
      return this.toothProcedures[tooth] || this.toothProcedures[String(tooth)] || [];
    },
    visibleProcedures(tooth) {
      return this.selectedProcedures(tooth).slice(0, 3);
    },
    toothSummary(tooth) {
      const items = [];
      const status = this.statusByTooth(tooth);
      if (status.value) items.push(status.label);
      if (this.selectedParts(tooth).length) items.push(`${this.selectedParts(tooth).length} regiões`);
      if (this.selectedProcedures(tooth).length) items.push(`${this.selectedProcedures(tooth).length} procedimentos`);
      if (this.observations[String(tooth)] || this.observations[tooth]) items.push("observação");
      return items.length ? items.join(" · ") : "Sem registro clínico";
    },
    toothClasses(tooth) {
      const key = String(tooth);
      const primaryProcedure = this.selectedProcedures(tooth)[0];
      return {
        "odonto-tooth--active": this.toothStatus[key] || this.selectedParts(tooth).length || this.selectedProcedures(tooth).length || this.observations[key],
        "odonto-tooth--selected": this.currentTooth === tooth,
        "odonto-tooth--has-procedure": this.selectedProcedures(tooth).length,
        [`odonto-tooth--procedure-${primaryProcedure}`]: Boolean(primaryProcedure),
      };
    },
    toothStyle(tooth) {
      const status = this.statusByTooth(tooth);
      if (!status.value) return {};
      return { borderColor: status.color, background: `${status.color}18`, color: status.color };
    },
    toothImage(tooth) {
      const filename = UPPER_TOOTH_IMAGES[tooth] || LOWER_TOOTH_IMAGES[tooth];
      return filename ? encodeURI(`/teeth/${filename}`) : "";
    },
    statusByTooth(tooth) {
      const value = Number(this.toothStatus[tooth] || this.toothStatus[String(tooth)] || 0);
      return this.statuses.find((item) => item.value === value) || { value: 0, label: "Selecionado", color: "#9e9e9e" };
    },
    partLabel(value) {
      return this.toothParts.find((part) => part.value === value)?.label || value;
    },
    procedureLabel(value) {
      return this.toothProceduresOptions.find((procedure) => procedure.value === value)?.label || value;
    },
    procedureShortLabel(value) {
      return this.toothProceduresOptions.find((procedure) => procedure.value === value)?.short || value.slice(0, 2).toUpperCase();
    },
    cleanMap(map) {
      return Object.keys(map).reduce((acc, key) => {
        const value = map[key];
        if (value !== null && value !== undefined && value !== "" && (!Array.isArray(value) || value.length)) acc[String(key)] = value;
        return acc;
      }, {});
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      if (!this.selectedTeeth.length) {
        this.$dialog.notify.error("Selecione ao menos um dente.", { position: "top-right", timeout: 4000 });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          patientId: this.form.patientId || null,
          procedureId: this.form.procedureId || null,
          specialtyId: this.form.specialtyId || null,
          toothNumber: this.selectedTeeth.join(","),
          toothStatus: this.cleanMap(this.toothStatus),
          toothPartStatus: this.cleanMap(this.toothPartStatus),
          toothProcedures: this.cleanMap(this.toothProcedures),
          toothObservations: this.cleanMap(this.observations),
        };

        if (this.form.id) {
          await axios.patch(`/odontogram/${this.form.id}`, payload);
          this.$dialog.notify.success("Odontograma atualizado com sucesso!", { position: "top-right", timeout: 3000 });
        } else {
          const response = await axios.post("/odontogram", payload);
          this.form.id = response.data.data?.id || null;
          this.$dialog.notify.success("Odontograma criado com sucesso!", { position: "top-right", timeout: 3000 });
        }
        this.$router.push("/odontogramas");
      } catch {
        this.$dialog.notify.error("Erro ao salvar odontograma.", { position: "top-right", timeout: 5000 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.odonto-form__grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 22px 22px 0;
}

.odonto-workspace {
  display: block;
  padding: 10px 22px 22px;
}

.odonto-records__title {
  color: #111816;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 10px;
}

.odonto-chart {
  --odonto-gap: clamp(4px, 0.45vw, 7px);
  --odonto-tooth-height: clamp(68px, 5.1vw, 82px);
  --odonto-tooth-image-height: clamp(40px, 3.25vw, 52px);
  --odonto-tooth-width: clamp(38px, 3.15vw, 52px);

  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  padding: 28px 18px;
}

.odonto-jaw-label {
  color: #69736d;
  font-size: 12px;
  font-weight: 700;
  margin: 6px 0 12px;
  text-align: center;
  text-transform: uppercase;
}

.odonto-row {
  align-items: center;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.odonto-row--lower {
  margin-top: 12px;
}

.odonto-quadrant {
  display: flex;
  flex: 0 1 auto;
  gap: var(--odonto-gap);
  min-width: 0;
}

.odonto-midline {
  background: #dfe5e0;
  border-radius: 2px;
  height: var(--odonto-tooth-height);
  margin: 0 clamp(7px, 0.8vw, 12px);
  width: 2px;
}

.odonto-tooth {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 10px;
  color: #69736d;
  cursor: pointer;
  display: flex;
  justify-content: center;
  flex: 0 0 var(--odonto-tooth-width);
  height: var(--odonto-tooth-height);
  padding: 8px 5px 18px;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  width: var(--odonto-tooth-width);
}

.odonto-tooth:hover {
  transform: translateY(-1px);
}

.odonto-tooth__image {
  display: block;
  height: var(--odonto-tooth-image-height);
  max-width: calc(var(--odonto-tooth-width) - 16px);
  object-fit: contain;
  opacity: 0.9;
  pointer-events: none;
}

.odonto-tooth span {
  bottom: 6px;
  font-size: 11px;
  font-weight: 800;
  left: 0;
  position: absolute;
  right: 0;
  text-align: center;
}

.odonto-tooth--active {
  box-shadow: 0 8px 18px rgba(31, 42, 36, 0.08);
}

.odonto-tooth--active .odonto-tooth__image {
  opacity: 1;
}

.odonto-tooth--selected {
  outline: 2px solid #a04dc2;
  outline-offset: 2px;
}

.odonto-tooth--has-procedure {
  overflow: hidden;
}

.odonto-tooth--procedure-crown::before,
.odonto-tooth--procedure-root_canal::before,
.odonto-tooth--procedure-extraction::before {
  content: "";
  pointer-events: none;
  position: absolute;
  z-index: 1;
}

.odonto-tooth--procedure-crown::before {
  background: #d9a441;
  border-radius: 999px;
  height: 4px;
  left: 14px;
  right: 14px;
  top: 8px;
}

.odonto-tooth--procedure-root_canal::before {
  background: #7e57c2;
  border-radius: 999px;
  bottom: 24px;
  left: 50%;
  top: 16px;
  transform: translateX(-50%);
  width: 3px;
}

.odonto-tooth--procedure-extraction::before {
  background: #d64b4b;
  height: 3px;
  left: 8px;
  right: 8px;
  top: 36px;
  transform: rotate(-28deg);
}

.odonto-tooth__procedure {
  align-items: center;
  border: 1px solid #fff;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(17, 24, 22, 0.18);
  color: #fff;
  display: flex;
  font-size: 8px;
  font-weight: 900;
  height: 16px;
  justify-content: center;
  line-height: 1;
  min-width: 16px;
  padding: 0 3px;
  position: absolute;
  right: 4px;
  top: 4px;
  z-index: 2;
}

.odonto-tooth__procedure:nth-of-type(2) {
  top: 22px;
}

.odonto-tooth__procedure:nth-of-type(3) {
  top: 40px;
}

.odonto-tooth__procedure--caries { background: #8d6e63; }
.odonto-tooth__procedure--restoration { background: #00a99d; }
.odonto-tooth__procedure--crown { background: #d9a441; }
.odonto-tooth__procedure--root_canal { background: #7e57c2; }
.odonto-tooth__procedure--extraction { background: #d64b4b; }
.odonto-tooth__procedure--implant { background: #2f80ed; }
.odonto-tooth__procedure--bridge { background: #546e7a; }
.odonto-tooth__procedure--veneer { background: #26a69a; }
.odonto-tooth__procedure--fracture { background: #f2994a; }
.odonto-tooth__procedure--mobility { background: #c2185b; }

.part {
  background: currentColor;
  border: 1px solid #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(17, 24, 22, 0.2);
  height: 8px;
  position: absolute;
  width: 8px;
}

.part-top { left: 50%; top: 9px; transform: translateX(-50%); }
.part-right { right: 8px; top: 33px; }
.part-bottom { bottom: 23px; left: 50%; transform: translateX(-50%); }
.part-left { left: 8px; top: 33px; }
.part-center { left: 50%; top: 33px; transform: translateX(-50%); }

.odonto-records {
  border-top: 1px solid #dfe5e0;
  padding: 18px 22px 0;
}

.odonto-record-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.odonto-record {
  background: #f7f8f6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
}

.odonto-record strong {
  color: #111816;
  font-size: 13px;
}

.odonto-record span,
.odonto-record small {
  font-size: 12px;
  font-weight: 600;
}

::v-deep .odonto-tooth-menu {
  border-radius: 10px;
  box-shadow: 0 22px 50px rgba(17, 24, 22, 0.18);
  overflow: hidden;
}

::v-deep .odonto-tooth-dropdown {
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 10px;
  color: #111816;
  max-height: min(720px, calc(100vh - 120px));
  overflow: hidden;
  width: 420px;
}

::v-deep .odonto-dropdown__header {
  align-items: center;
  background: linear-gradient(180deg, #fbfafc 0%, #fff 100%);
  border-bottom: 1px solid #e8ece8;
  display: flex;
  gap: 14px;
  padding: 16px;
}

::v-deep .odonto-dropdown__preview {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 10px;
  color: #69736d;
  display: flex;
  flex: 0 0 auto;
  height: 68px;
  justify-content: center;
  padding: 7px 5px 17px;
  position: relative;
  width: 48px;
}

::v-deep .odonto-dropdown__preview img {
  display: block;
  height: 42px;
  max-width: 30px;
  object-fit: contain;
}

::v-deep .odonto-dropdown__preview span {
  bottom: 5px;
  font-size: 10px;
  font-weight: 900;
  left: 0;
  position: absolute;
  right: 0;
  text-align: center;
}

::v-deep .odonto-dropdown__title {
  color: #111816;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.1;
  margin-bottom: 4px;
}

::v-deep .odonto-dropdown__subtitle {
  color: #69736d;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

::v-deep .odonto-dropdown__body {
  display: grid;
  gap: 10px;
  max-height: calc(min(720px, calc(100vh - 120px)) - 158px);
  overflow-y: auto;
  padding: 12px 14px;
}

::v-deep .odonto-dropdown__group {
  background: #f7f8f6;
  border: 1px solid #e4e8e4;
  border-radius: 8px;
  padding: 10px;
}

::v-deep .odonto-dropdown__section {
  color: #69736d;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 8px;
  text-transform: uppercase;
}

::v-deep .odonto-dropdown__status-grid {
  display: grid;
  gap: 7px;
  grid-template-columns: 1fr 1fr;
}

::v-deep .odonto-dropdown__status {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  color: #111816;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  min-height: 36px;
  padding: 0 10px;
}

::v-deep .odonto-dropdown__status span {
  border-radius: 50%;
  height: 10px;
  width: 10px;
}

::v-deep .odonto-dropdown__status.active,
::v-deep .odonto-dropdown__parts button.active,
::v-deep .odonto-dropdown__procedures button.active {
  background: #f8effc;
  border-color: #a04dc2;
  box-shadow: inset 0 0 0 1px #a04dc2;
  color: #7e349f;
}

::v-deep .odonto-dropdown__parts {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

::v-deep .odonto-dropdown__parts button {
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 999px;
  color: #69736d;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  min-height: 30px;
  padding: 0 10px;
}

::v-deep .odonto-dropdown__procedures {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr;
}

::v-deep .odonto-dropdown__procedures button {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  color: #111816;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 7px;
  min-height: 38px;
  padding: 0 9px;
  text-align: left;
}

::v-deep .odonto-dropdown__procedures span {
  align-items: center;
  border-radius: 999px;
  color: #fff;
  display: flex;
  flex: 0 0 auto;
  font-size: 9px;
  font-weight: 900;
  height: 20px;
  justify-content: center;
  width: 24px;
}

::v-deep .odonto-dropdown__footer {
  background: #fff;
  border-top: 1px solid #e8ece8;
  padding: 12px 14px;
}

::v-deep .odonto-dropdown__footer .v-btn {
  border-radius: 8px;
  font-weight: 800;
  min-height: 38px;
}

@media only screen and (max-width: 1200px) {
  .odonto-form__grid,
  .odonto-record-grid {
    grid-template-columns: 1fr;
  }

  .odonto-chart {
    overflow-x: auto;
  }
}
</style>
